import { describeError, useApi } from '@classgo/app-kit';
import type { AttendanceDay, AttendanceStatus } from '@classgo/shared';
import { Badge, type BadgeTone, Button, EmptyState, Input, Select, Table } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { listQuery } from '../../academic/api';
import { attendanceApi, attendanceQueries, refusalOf } from '../../attendance/api';
import { useT } from '../../i18n';
import { today } from '../../lib/money';
import { can } from '../../shell';
import { useTenant } from '../../tenant/tenant-provider';
import { timetableQueries } from '../../timetable/api';
import { countMarks, nextStatus, rollName, sessionsOn } from './support';

const TONES: Record<AttendanceStatus, BadgeTone> = {
  present: 'success',
  absent: 'danger',
  late: 'warning',
  excused: 'info',
};

type Row = AttendanceDay['rows'][number];

/**
 * The roll as the school reads it: one row per enrolled student with the stored mark and note.
 */
const ReadRoll = ({ roll, loading }: { roll: AttendanceDay | undefined; loading: boolean }) => {
  const t = useT();
  return (
    <Table<Row>
      caption={t('attendance.caption')}
      rows={roll?.rows ?? []}
      rowKey={(row) => row.studentId}
      loading={loading}
      empty={<EmptyState title={t('attendance.empty')} />}
      columns={[
        {
          key: 'student',
          header: t('attendance.column.student'),
          cell: (row) => <span className="font-medium text-gray-900">{rollName(row)}</span>,
        },
        {
          key: 'status',
          header: t('attendance.column.status'),
          cell: (row) =>
            row.status ? (
              <Badge tone={TONES[row.status]}>{t(`attendance.status.${row.status}`)}</Badge>
            ) : (
              <Badge tone="gray">{t('attendance.status.unmarked')}</Badge>
            ),
        },
        { key: 'note', header: t('attendance.column.note'), cell: (row) => row.note ?? '' },
      ]}
    />
  );
};

/**
 * The roll as the app's session screen marks it (#150), for a coordinator on a phone: everyone
 * present by default, one tap per student cycles the mark, one "Guardar asistencia" sends every
 * mark in one call. The API is idempotent, so saving again reports 0 changes; a stored note
 * travels back with its mark so a save never clears it.
 */
const MarkingRoll = ({
  roll,
  groupId,
  date,
  blockId,
}: {
  roll: AttendanceDay;
  groupId: string;
  date: string;
  blockId: string | null;
}) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const [marks, setMarks] = useState<Record<string, AttendanceStatus>>({});
  const [saved, setSaved] = useState<number | null>(null);
  useEffect(() => {
    setMarks(Object.fromEntries(roll.rows.map((row) => [row.studentId, row.status ?? 'present'])));
  }, [roll]);
  const save = useMutation({
    mutationFn: () =>
      attendanceApi(api).mark({
        groupId,
        date,
        blockId,
        marks: roll.rows.map((row) => ({
          studentId: row.studentId,
          status: marks[row.studentId] ?? 'present',
          ...(row.note ? { note: row.note } : {}),
        })),
      }),
    onSuccess: async (result) => {
      setSaved(result.changed);
      queries.setQueryData(
        attendanceQueries.roll(api, groupId, date, blockId).queryKey,
        (): AttendanceDay => ({
          groupId: result.groupId,
          date: result.date,
          blockId: result.blockId,
          rows: result.rows,
          summary: result.summary,
        }),
      );
      await queries.invalidateQueries({ queryKey: ['school', 'attendance', 'alerts'] });
    },
  });
  const tap = (studentId: string) => {
    setSaved(null);
    setMarks((current) => ({
      ...current,
      [studentId]: nextStatus(current[studentId] ?? 'present'),
    }));
  };
  const allPresent = () => {
    setSaved(null);
    setMarks(Object.fromEntries(roll.rows.map((row) => [row.studentId, 'present'])));
  };
  const counts = countMarks(marks);

  if (roll.rows.length === 0) return <EmptyState title={t('attendance.empty')} />;
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">{t('attendance.mark.hint')}</p>
      <ul aria-label={t('attendance.caption')} className="divide-y divide-gray-100">
        {roll.rows.map((row) => {
          const status = marks[row.studentId] ?? 'present';
          return (
            <li key={row.studentId}>
              <button
                type="button"
                onClick={() => tap(row.studentId)}
                className="control-touch flex w-full items-center justify-between gap-3 rounded-control px-2 py-2 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <span className="min-w-0">
                  <span className="block font-medium text-gray-900">{rollName(row)}</span>
                  {row.note ? (
                    <span className="block text-xs text-gray-600">{row.note}</span>
                  ) : null}
                </span>
                <Badge tone={TONES[status]} className="min-w-[6.5rem] shrink-0 justify-center">
                  {t(`attendance.mark.${status}`)}
                </Badge>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="text-sm text-gray-600">{t('attendance.mark.counts', counts)}</p>
      {saved !== null ? (
        <p
          role="status"
          className="rounded-control bg-success-100 px-3 py-2 text-sm text-success-700"
        >
          {t('attendance.mark.saved', { count: saved })}
        </p>
      ) : null}
      {save.isError ? (
        <p role="alert" className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700">
          {t('attendance.mark.failed')} {refusalOf(save.error) ?? describeError(t, save.error)}
        </p>
      ) : null}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={allPresent}>
          {t('attendance.mark.allPresent')}
        </Button>
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          {t('attendance.mark.save')}
        </Button>
      </div>
    </div>
  );
};

/**
 * The roll tab: a group of the sede, a date and, when the group has classes that day in the
 * timetable, the session (or the whole day); then the roll to read, or to mark with
 * `attendance:write`.
 */
export const RollTab = ({ canWrite }: { canWrite: boolean }) => {
  const t = useT();
  const api = useApi();
  const { session, sede } = useTenant();
  const groups = useQuery(listQuery(api, 'groups', { sedeId: sede.id }));
  const groupItems = (groups.data?.items ?? []).filter((group) => group.sedeId === sede.id);
  const [groupId, setGroupId] = useState('');
  const group = groupItems.find((candidate) => candidate.id === groupId) ?? groupItems[0];
  const [date, setDate] = useState(today());
  const [wantedBlockId, setWantedBlockId] = useState('');
  const withSessions = can(session, 'timetable:read');
  const week = useQuery({
    ...timetableQueries.group(api, group?.id ?? ''),
    enabled: withSessions && group !== undefined,
  });
  const sessions = sessionsOn(week.data?.blocks ?? [], date);
  const blockId = sessions.some((block) => block.id === wantedBlockId) ? wantedBlockId : '';
  const roll = useQuery(attendanceQueries.roll(api, group?.id ?? '', date, blockId || null));
  const summary = roll.data?.summary;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Select
          label={t('attendance.filter.group')}
          value={group?.id ?? ''}
          onChange={(event) => setGroupId(event.target.value)}
          options={groupItems.map((item) => ({ value: item.id, label: item.name }))}
        />
        <Input
          label={t('attendance.filter.date')}
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
        {withSessions ? (
          <Select
            label={t('attendance.filter.session')}
            value={blockId}
            onChange={(event) => setWantedBlockId(event.target.value)}
            options={[
              { value: '', label: t('attendance.session.day') },
              ...sessions.map((block) => ({
                value: block.id,
                label: `${block.startsAt}–${block.endsAt} · ${block.subjectName}`,
              })),
            ]}
            help={
              week.data && sessions.length === 0 && date !== ''
                ? t('attendance.session.none')
                : undefined
            }
          />
        ) : null}
      </div>
      {summary && !canWrite ? (
        <p role="status" className="text-sm text-gray-600">
          {t('attendance.summary', summary)}
        </p>
      ) : null}
      {groups.isError ? (
        <EmptyState title={t('entity.error.title')} description={describeError(t, groups.error)} />
      ) : roll.isError ? (
        <EmptyState title={t('entity.error.title')} description={describeError(t, roll.error)} />
      ) : canWrite ? (
        roll.data && group ? (
          <MarkingRoll
            key={`${group.id}|${date}|${blockId}`}
            roll={roll.data}
            groupId={group.id}
            date={date}
            blockId={blockId || null}
          />
        ) : (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        )
      ) : (
        <ReadRoll roll={roll.data} loading={roll.isPending && group !== undefined} />
      )}
    </div>
  );
};
